import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import { ApiResponse } from '../models/api.model';
import { environment } from '../../../environments/environment';
import {
  UserNotificationDto, UnreadCountDto, NtfPreferenceDto,
  NtfTemplateListDto, NtfTemplateDetailDto, SaveNtfTemplateRequest,
  NtfTypeDto, NtfChannelDto, SendOtpRequest, VerifyOtpRequest, OtpResultDto,
  NtfConfigurationDto, SaveNtfConfigRequest,
  EmailConfigDto, SmsConfigDto, SaveSmsConfigRequest,
  OtpConfigDto, PushConfigDto, SavePushConfigRequest,
  BroadcastListDto, SaveBroadcastRequest,
  CampaignListDto, CampaignDetailDto, SaveCampaignRequest,
  NtfDeliveryReportDto, NtfLogDto, FailedNotificationDto, PagedResult,
  SendNotificationRequest, RegisterPushTokenRequest
} from '../models/notification.model';

const API = `${environment.apiUrl}/notification`;

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private http = inject(HttpClient);

  // ── Unread count reactive signal ─────────────────────────────────────────
  private _unreadCount = signal(0);
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly hasUnread   = computed(() => this._unreadCount() > 0);

  private unreadInFlight = false;

  /** Fetches the server count. Ignored while a previous fetch is still pending, so callers can't stack duplicates. */
  refreshUnreadCount(): void {
    if (this.unreadInFlight) return;
    this.unreadInFlight = true;
    this.getUnreadCount().pipe(finalize(() => this.unreadInFlight = false)).subscribe({
      next: res => this._unreadCount.set(res?.data?.count ?? 0),
      error: () => {}
    });
  }

  /** Clears the cached count (logout / user change) so the next user never sees it. */
  resetUnread(): void {
    this._unreadCount.set(0);
  }

  decrementUnread(): void {
    const current = this._unreadCount();
    if (current > 0) this._unreadCount.set(current - 1);
  }

  // ── Inbox ────────────────────────────────────────────────────────────────

  getInbox(page = 1, pageSize = 20): Observable<ApiResponse<UserNotificationDto[]>> {
    const params = new HttpParams().set('page', page).set('pageSize', pageSize);
    return this.http.get<ApiResponse<UserNotificationDto[]>>(`${API}/inbox`, { params });
  }

  getUnreadCount(): Observable<ApiResponse<UnreadCountDto>> {
    return this.http.get<ApiResponse<UnreadCountDto>>(`${API}/inbox/unread-count`);
  }

  markRead(id: number): Observable<ApiResponse<boolean>> {
    return this.http.patch<ApiResponse<boolean>>(`${API}/inbox/${id}/read`, {}).pipe(
      tap(() => this.decrementUnread())
    );
  }

  markAllRead(): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/inbox/mark-all-read`, {}).pipe(
      tap(() => this._unreadCount.set(0))
    );
  }

  deleteNotification(id: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${API}/inbox/${id}`);
  }

  // ── Preferences ──────────────────────────────────────────────────────────

  getPreferences(): Observable<ApiResponse<NtfPreferenceDto>> {
    return this.http.get<ApiResponse<NtfPreferenceDto>>(`${API}/preferences`);
  }

  savePreferences(req: NtfPreferenceDto): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${API}/preferences`, req);
  }

  // ── OTP ──────────────────────────────────────────────────────────────────

  sendOtp(req: SendOtpRequest): Observable<ApiResponse<OtpResultDto>> {
    return this.http.post<ApiResponse<OtpResultDto>>(`${API}/send-otp`, req);
  }

  verifyOtp(req: VerifyOtpRequest): Observable<ApiResponse<OtpResultDto>> {
    return this.http.post<ApiResponse<OtpResultDto>>(`${API}/verify-otp`, req);
  }

  // ── Master data ──────────────────────────────────────────────────────────

  getTypes(): Observable<ApiResponse<NtfTypeDto[]>> {
    return this.http.get<ApiResponse<NtfTypeDto[]>>(`${API}/types`);
  }

  getChannels(): Observable<ApiResponse<NtfChannelDto[]>> {
    return this.http.get<ApiResponse<NtfChannelDto[]>>(`${API}/channels`);
  }

  // ── Templates ────────────────────────────────────────────────────────────

  getTemplates(tenantId?: number): Observable<ApiResponse<NtfTemplateListDto[]>> {
    const params = tenantId ? new HttpParams().set('tenantId', tenantId) : undefined;
    return this.http.get<ApiResponse<NtfTemplateListDto[]>>(`${API}/templates`, { params });
  }

  getTemplate(id: number): Observable<ApiResponse<NtfTemplateDetailDto>> {
    return this.http.get<ApiResponse<NtfTemplateDetailDto>>(`${API}/templates/${id}`);
  }

  saveTemplate(req: SaveNtfTemplateRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(`${API}/templates`, req);
  }

  deleteTemplate(id: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${API}/templates/${id}`);
  }

  // ── Push token ────────────────────────────────────────────────────────────

  registerPushToken(req: RegisterPushTokenRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/push-token`, req);
  }

  // ── Channel Configuration ─────────────────────────────────────────────────

  getConfig(tenantId?: number): Observable<ApiResponse<NtfConfigurationDto>> {
    const params = tenantId ? new HttpParams().set('tenantId', tenantId) : undefined;
    return this.http.get<ApiResponse<NtfConfigurationDto>>(`${API}/config`, { params });
  }

  saveConfig(req: SaveNtfConfigRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${API}/config`, req);
  }

  // ── Email Config ──────────────────────────────────────────────────────────

  /**
   * Returns the one central SMTP identity the whole application sends from, as a
   * single-element list. There is deliberately no saveEmailConfig() counterpart: SMTP
   * settings live in server configuration (`Smtp__*`), not in the database and not per
   * tenant, and POST /notification/config/email rejects any attempt to change them.
   */
  getEmailConfigs(): Observable<ApiResponse<EmailConfigDto[]>> {
    return this.http.get<ApiResponse<EmailConfigDto[]>>(`${API}/config/email`);
  }

  testEmail(toEmail: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/config/email/test`, { toEmail });
  }

  // ── SMS Config ────────────────────────────────────────────────────────────

  getSmsConfigs(tenantId?: number): Observable<ApiResponse<SmsConfigDto[]>> {
    const params = tenantId ? new HttpParams().set('tenantId', tenantId) : undefined;
    return this.http.get<ApiResponse<SmsConfigDto[]>>(`${API}/config/sms`, { params });
  }

  saveSmsConfig(req: SaveSmsConfigRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(`${API}/config/sms`, req);
  }

  testSms(phoneNumber: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/config/sms/test`, { phoneNumber });
  }

  // ── OTP Config ────────────────────────────────────────────────────────────

  getOtpConfig(tenantId?: number): Observable<ApiResponse<OtpConfigDto>> {
    const params = tenantId ? new HttpParams().set('tenantId', tenantId) : undefined;
    return this.http.get<ApiResponse<OtpConfigDto>>(`${API}/config/otp`, { params });
  }

  saveOtpConfig(req: OtpConfigDto): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${API}/config/otp`, req);
  }

  // ── Push Config ────────────────────────────────────────────────────────────

  getPushConfigs(tenantId?: number): Observable<ApiResponse<PushConfigDto[]>> {
    const params = tenantId ? new HttpParams().set('tenantId', tenantId) : undefined;
    return this.http.get<ApiResponse<PushConfigDto[]>>(`${API}/config/push`, { params });
  }

  savePushConfig(req: SavePushConfigRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(`${API}/config/push`, req);
  }

  // ── Broadcasts ────────────────────────────────────────────────────────────

  getBroadcasts(tenantId?: number): Observable<ApiResponse<BroadcastListDto[]>> {
    const params = tenantId ? new HttpParams().set('tenantId', tenantId) : undefined;
    return this.http.get<ApiResponse<BroadcastListDto[]>>(`${API}/broadcasts`, { params });
  }

  saveBroadcast(req: SaveBroadcastRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(`${API}/broadcasts`, req);
  }

  sendBroadcast(id: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/broadcasts/${id}/send`, {});
  }

  // ── Campaigns ─────────────────────────────────────────────────────────────

  getCampaigns(tenantId?: number): Observable<ApiResponse<CampaignListDto[]>> {
    const params = tenantId ? new HttpParams().set('tenantId', tenantId) : undefined;
    return this.http.get<ApiResponse<CampaignListDto[]>>(`${API}/campaigns`, { params });
  }

  getCampaign(id: number): Observable<ApiResponse<CampaignDetailDto>> {
    return this.http.get<ApiResponse<CampaignDetailDto>>(`${API}/campaigns/${id}`);
  }

  saveCampaign(req: SaveCampaignRequest): Observable<ApiResponse<number>> {
    return this.http.post<ApiResponse<number>>(`${API}/campaigns`, req);
  }

  executeCampaign(id: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/campaigns/${id}/execute`, {});
  }

  cancelCampaign(id: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/campaigns/${id}/cancel`, {});
  }

  // ── Reports ───────────────────────────────────────────────────────────────

  getDeliveryReport(from: string, to: string, tenantId?: number): Observable<ApiResponse<NtfDeliveryReportDto>> {
    let params = new HttpParams().set('from', from).set('to', to);
    if (tenantId) params = params.set('tenantId', tenantId);
    return this.http.get<ApiResponse<NtfDeliveryReportDto>>(`${API}/reports/delivery`, { params });
  }

  getHistory(page = 1, pageSize = 20): Observable<ApiResponse<PagedResult<NtfLogDto>>> {
    const params = new HttpParams().set('page', page).set('pageSize', pageSize);
    return this.http.get<ApiResponse<PagedResult<NtfLogDto>>>(`${API}/reports/history`, { params });
  }

  getFailedNotifications(page = 1, pageSize = 20): Observable<ApiResponse<PagedResult<FailedNotificationDto>>> {
    const params = new HttpParams().set('page', page).set('pageSize', pageSize);
    return this.http.get<ApiResponse<PagedResult<FailedNotificationDto>>>(`${API}/reports/failed`, { params });
  }

  retryFailed(id: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/reports/failed/${id}/retry`, {});
  }

  resolveFailed(id: number): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/reports/failed/${id}/resolve`, {});
  }

  // ── Admin: direct send ────────────────────────────────────────────────────

  sendNotification(req: SendNotificationRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${API}/send`, req);
  }
}
